import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAccount } from 'wagmi';
import { referralApi } from '../api/referral';
import { useAuth } from '../contexts/AuthContext';

type TabKey = 'home' | 'buy' | 'network' | 'payouts' | 'account' | 'kyc';

const shortAddr = (a?: string) => (a ? a.slice(0, 6) + '…' + a.slice(-4) : '');

const KYC_STYLE: Record<string, { label: string; color: string; bg: string }> = {
  APPROVED: { label: 'Verified', color: '#34D399', bg: 'rgba(52,211,153,0.15)' },
  PENDING: { label: 'Pending review', color: '#FBBF24', bg: 'rgba(251,191,36,0.15)' },
  REJECTED: { label: 'Rejected', color: '#F87171', bg: 'rgba(248,113,113,0.15)' },
  NONE: { label: 'Not started', color: '#8B82A8', bg: 'rgba(139,130,168,0.15)' },
};

function Card({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <div className={`rounded-3xl ${className}`} style={{ backgroundColor: '#15122e', border: '1px solid rgba(255,255,255,0.06)' }}>{children}</div>;
}

function Row({ label, right, onClick }: { label: string; right: React.ReactNode; onClick?: () => void }) {
  return (
    <button onClick={onClick} disabled={!onClick} className="w-full flex items-center justify-between px-5 py-4 text-left" style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
      <span className="text-sm">{label}</span>
      <span className="flex items-center gap-2 text-sm" style={{ color: '#8B82A8' }}>{right}{onClick && <span style={{ color: '#52526E' }}>›</span>}</span>
    </button>
  );
}

export default function Account({ onNavigate }: { onNavigate: (t: TabKey) => void }) {
  const { user, logout } = useAuth();
  const { data } = useQuery({ queryKey: ['referral-me'], queryFn: referralApi.me });
  const { address, isConnected } = useAccount();
  const [copied, setCopied] = useState(false);

  // Prefer whatever the API reliably gives us; fall back across sources.
  const email = user?.email ?? '';
  const referralCode = data?.referralCode ?? user?.referralCode ?? '';
  const avatarChar = (email.trim()[0] || referralCode.trim()[0] || 'A').toUpperCase();
  const kyc = KYC_STYLE[data?.kycStatus ?? 'NONE'] ?? KYC_STYLE.NONE;

  async function copyCode() {
    if (!referralCode) return;
    try { await navigator.clipboard.writeText(referralCode); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch {}
  }

  return (
    <div className="space-y-5 pb-2">
      <div>
        <div className="text-[11px] tracking-widest" style={{ color: '#8B82A8' }}>SETTINGS</div>
        <h1 className="text-3xl font-bold leading-tight" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>Account</h1>
      </div>

      {/* Profile */}
      <Card className="p-5 flex items-center gap-4">
        <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-lg font-bold text-white" style={{ background: 'linear-gradient(135deg,#8B5CF6,#EC4899)' }}>{avatarChar}</div>
        <div className="min-w-0">
          <div className="text-sm truncate">{email || 'Your account'}</div>
          <div className="text-xs mt-0.5" style={{ color: '#8B82A8' }}>Member</div>
        </div>
      </Card>

      {/* Referral code */}
      <Card className="p-5">
        <div className="text-[11px] tracking-widest mb-2" style={{ color: '#8B82A8' }}>YOUR REFERRAL CODE</div>
        <div className="flex items-center justify-between">
          <span className="text-lg font-bold" style={{ fontFamily: '"JetBrains Mono", monospace', color: '#c4b5fd' }}>{referralCode || '—'}</span>
          <button onClick={copyCode} disabled={!referralCode} className="rounded-xl px-3 py-2 text-xs font-semibold text-white" style={{ background: 'linear-gradient(135deg,#8B5CF6,#EC4899)', opacity: referralCode ? 1 : 0.5 }}>{copied ? 'Copied' : 'Copy'}</button>
        </div>
      </Card>

      {/* Status rows */}
      <Card className="overflow-hidden">
        <Row label="Identity (KYC)" onClick={() => onNavigate('kyc')} right={<span className="text-[11px] px-2 py-1 rounded-full font-semibold" style={{ backgroundColor: kyc.bg, color: kyc.color }}>{kyc.label}</span>} />
        <Row label="Payout wallet" onClick={() => onNavigate('payouts')} right={isConnected ? <span style={{ fontFamily: '"JetBrains Mono", monospace' }}>{shortAddr(address)}</span> : 'Not connected'} />
        <Row label="Help & support" onClick={() => window.open('https://altcoinsmoney.com/contact', '_blank')} right="" />
      </Card>

      {/* Logout */}
      <button onClick={() => logout()} className="w-full rounded-2xl py-3.5 text-sm font-semibold" style={{ backgroundColor: 'rgba(248,113,113,0.12)', color: '#F87171' }}>
        Log out
      </button>

      <div className="text-center text-[11px]" style={{ color: '#52526E' }}>AltCoinsMoney · v1.0.0</div>
    </div>
  );
}